"use client";

import { useEffect, useLayoutEffect, useState, type ReactNode } from "react";
import { ArrowRightLeft, RotateCcw } from "lucide-react";
import { AeraButton, ModalCloseButton, ModalShell } from "@aera/ui";

const RECORD_DRAWER_PANEL_SELECTOR =
  "[data-aera-modal-panel].record-drawer-modal-panel";

function useMinWidthMd() {
  const [matches, setMatches] = useState(false);

  useEffect(() => {
    const mq = window.matchMedia("(min-width: 768px)");
    const update = () => setMatches(mq.matches);
    update();
    mq.addEventListener("change", update);
    return () => mq.removeEventListener("change", update);
  }, []);

  return matches;
}

function formatBaht(value: number): string {
  const hasFraction = Math.round(value * 100) % 100 !== 0;
  return value.toLocaleString("th-TH", {
    minimumFractionDigits: hasFraction ? 2 : 0,
    maximumFractionDigits: 2,
  });
}

/** Parse history lines like `ย้าย 100.00 บาท จาก อิจิโกะ ไป อิโนะอุเอะ`. */
export function formatMoveUndoSummaryLine(
  title: string | null | undefined,
  detailLines: string[],
): string | null {
  const isMove = (title || "").includes("ย้ายยอด");
  if (!isMove) return null;
  for (const line of detailLines) {
    const matched = line.match(
      /ย้าย\s+([0-9.,]+)\s*บาท\s+จาก\s+(.+?)\s+ไป\s+(.+)/,
    );
    if (!matched) continue;
    const amount = Number.parseFloat(matched[1].replace(/,/g, ""));
    const fromName = matched[2].trim();
    const toName = matched[3].trim();
    if (!Number.isFinite(amount) || !fromName || !toName) continue;
    return `ย้อนย้ายยอด ${fromName} ฿${formatBaht(amount)} เป็น ${toName}`;
  }
  return null;
}

export function MoneyUndoConfirmSheet({
  isOpen,
  onClose,
  busy = false,
  title,
  summary,
  detailLines = [],
  metaLine,
  error,
  blocked = false,
  blockedMessage,
  confirmLabel = "ยืนยันย้อนกลับ",
  confirmLoadingLabel = "กำลังย้อนกลับ…",
  onConfirm,
  children,
}: {
  isOpen: boolean;
  onClose: () => void;
  busy?: boolean;
  /** Impact / operation title, e.g. ย้ายยอดที่จับคู่ */
  title?: string | null;
  /** One-line summary under the header (move-style when provided). */
  summary?: ReactNode;
  detailLines?: string[];
  metaLine?: string | null;
  error?: string | null;
  blocked?: boolean;
  blockedMessage?: string | null;
  confirmLabel?: string;
  confirmLoadingLabel?: string;
  onConfirm: () => void;
  children?: ReactNode;
}) {
  const isDesktop = useMinWidthMd();
  const [scopedHost, setScopedHost] = useState<Element | null>(null);

  useLayoutEffect(() => {
    if (!isDesktop) {
      setScopedHost(null);
      return;
    }
    if (!isOpen) return;
    setScopedHost(document.querySelector(RECORD_DRAWER_PANEL_SELECTOR));
  }, [isDesktop, isOpen]);

  const isScoped = isDesktop && scopedHost != null;
  // Outside a parent modal: center on desktop. Inside drawer / on mobile: bottom sheet.
  const align = isScoped || !isDesktop ? "bottom" : "center";
  const isCentered = align === "center";
  const isMove = (title || "").includes("ย้ายยอด");
  const moveSummary =
    summary ?? formatMoveUndoSummaryLine(title, detailLines);
  const headerTitle = isMove
    ? "ยืนยันย้อนกลับย้ายยอด"
    : "ยืนยันย้อนกลับการแก้ไข";
  const HeaderIcon = isMove ? ArrowRightLeft : RotateCcw;

  const close = () => {
    if (busy) return;
    onClose();
  };

  const bodyLines =
    moveSummary && isMove
      ? detailLines.filter((line) => !/ย้าย\s+[0-9.,]+\s*บาท\s+จาก/.test(line))
      : detailLines;

  return (
    <ModalShell
      isOpen={isOpen}
      onClose={close}
      onExited={() => setScopedHost(null)}
      align={align}
      scopedHost={isScoped ? scopedHost : null}
      preventBackdropClose={busy}
      zIndexClass="z-[10050]"
      panelClassName={[
        "flex w-full flex-col overflow-hidden border-aera-lightgray bg-aera-white shadow-xl",
        isCentered
          ? "mx-4 max-h-[80dvh] max-w-md rounded-4xl border"
          : [
              "rounded-t-2xl rounded-b-none border-t md:rounded-b-4xl",
              isScoped ? "max-h-[85%]" : "max-h-[80dvh]",
            ].join(" "),
      ].join(" ")}
    >
      {!isCentered ? (
        <div className="flex shrink-0 justify-center pb-1 pt-2">
          <div className="h-1 w-10 rounded-full bg-aera-lightgray" aria-hidden />
        </div>
      ) : (
        <div className="shrink-0 pt-2" aria-hidden />
      )}

      <header className="flex shrink-0 items-start justify-between gap-3 px-4 pb-4">
        <div className="min-w-0">
          <h2 className="text-xl font-bold text-aera-charcoal">
            <span
              className={`flex items-center gap-2 ${
                isMove ? "text-aera-amberdark" : "text-aera-red"
              }`}
            >
              <HeaderIcon
                className="h-5 w-5 shrink-0"
                strokeWidth={2}
                aria-hidden
              />
              {headerTitle}
            </span>
          </h2>
          {moveSummary ? (
            <p className="mt-1 text-sm leading-relaxed text-aera-gray">
              {moveSummary}
            </p>
          ) : title ? (
            <p className="mt-1 text-sm font-semibold text-aera-charcoal">
              {title}
            </p>
          ) : null}
          {metaLine ? (
            <p className="mt-0.5 text-xs text-aera-lightslate">{metaLine}</p>
          ) : null}
        </div>
        <ModalCloseButton
          onClose={close}
          disabled={busy}
          className="-mr-1 shrink-0"
        />
      </header>

      <div className="min-h-0 flex-1 overflow-y-auto px-4 py-3 text-aera-charcoal">
        {children}
        {bodyLines.length > 0 ? (
          <ul className="space-y-1.5">
            {bodyLines.map((line) => (
              <li
                key={line}
                className="text-sm leading-relaxed text-aera-gray before:mr-1.5 before:text-aera-lightslate before:content-['•']"
              >
                {line}
              </li>
            ))}
          </ul>
        ) : null}
        {blocked ? (
          <p className="mt-3 rounded-xl bg-aera-red/10 px-3 py-2 text-xs font-medium text-aera-red">
            {blockedMessage || "ไม่สามารถย้อนกลับได้ตอนนี้"}
          </p>
        ) : null}
        {error ? (
          <p className="mt-3 text-xs font-medium text-aera-red">{error}</p>
        ) : null}
      </div>

      <div className="shrink-0 space-y-3 px-4 pt-3 pb-[max(0.75rem,env(safe-area-inset-bottom))]">
        <AeraButton
          type="button"
          variant={isMove ? "primary" : "danger"}
          fontWeight="medium"
          fullWidth
          className="cursor-pointer"
          loading={busy}
          disabled={busy || blocked}
          onClick={onConfirm}
        >
          {busy ? confirmLoadingLabel : confirmLabel}
        </AeraButton>
        <AeraButton
          type="button"
          variant="outline"
          fontWeight="medium"
          fullWidth
          className="cursor-pointer"
          disabled={busy}
          onClick={close}
        >
          ปิด
        </AeraButton>
      </div>
    </ModalShell>
  );
}
